/**
 * BookingsPage — Clerk's allocation pipeline.
 *
 * All data comes from the real allocations API. No localStorage. No bookingsStore.
 *
 * Pipeline view:
 *   Awaiting Response  — pending + conference_requested (clerk is waiting on barrister)
 *   Accepted           — barrister accepted the brief
 *   Declined           — barrister declined or allocation was withdrawn
 *   All                — full history
 */

import { useState, useMemo, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { format, parseISO } from 'date-fns';
import {
  Briefcase,
  CheckCircle,
  Clock,
  MagnifyingGlass as Search,
  Plus,
  Warning,
  X,
  ArrowCounterClockwise,
  ChatCenteredDots,
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { PageHeader } from '@/components/shared/PageHeader';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { StatCard } from '@/components/shared/StatCard';
import { RowActions } from '@/components/shared/RowActions';
import { useAuthStore } from '@/features/auth/store';
import { useApiBarristersStore } from '@/lib/barristersApi';
import {
  listAllocations,
  listAwaiting,
  offerAllocation,
  withdrawAllocation,
  DECLINE_REASON_LABEL,
  type Allocation,
  type AllocationStatus,
} from '@/lib/allocationsApi';
import { fetchMatters } from '@/lib/mattersApi';
import type { Matter } from '@/lib/mattersStore';
import { ScrollLock } from '@/components/shared/ScrollLock';

/* ─── Constants ─────────────────────────────────────────────── */

type Tab = 'awaiting' | 'accepted' | 'declined' | 'all';

const LABEL_CLS =
  'mb-1.5 block text-xs font-semibold uppercase tracking-wider text-muted-foreground';
const INPUT_CLS =
  'h-10 w-full rounded-md border border-border bg-card px-3 text-sm focus:border-brand-600 focus:outline-none focus:ring-2 focus:ring-brand-100';

/* ─── Helpers ────────────────────────────────────────────────── */

function barristerDisplayName(a: Allocation): string {
  const u = a.barrister?.user;
  if (!u) return '—';
  const suffix = u.memberType === 'silk' ? ' KC' : '';
  return `${u.firstName} ${u.lastName}${suffix}`;
}

function matterDisplayName(a: Allocation): string {
  const c = a.case;
  if (!c) return '—';
  return c.caseNumber ? `${c.caseNumber} — ${c.caseName}` : c.caseName;
}

function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  try {
    return format(parseISO(iso), 'd MMM yyyy');
  } catch {
    return '—';
  }
}

function statusBadge(status: AllocationStatus) {
  switch (status) {
    case 'pending':
      return (
        <Badge variant="info">
          <Clock className="mr-1 h-3 w-3" />
          Awaiting response
        </Badge>
      );
    case 'conference_requested':
      return (
        <Badge variant="warning">
          <ChatCenteredDots className="mr-1 h-3 w-3" />
          Conference requested
        </Badge>
      );
    case 'accepted':
      return (
        <Badge variant="success">
          <CheckCircle className="mr-1 h-3 w-3" />
          Accepted
        </Badge>
      );
    case 'declined':
      return <Badge variant="danger">Declined</Badge>;
    case 'withdrawn':
      return (
        <Badge variant="default">
          <ArrowCounterClockwise className="mr-1 h-3 w-3" />
          Withdrawn
        </Badge>
      );
    default:
      return <Badge>{status}</Badge>;
  }
}

/* ─── Offer Brief Modal ──────────────────────────────────────── */

interface OfferModalProps {
  unallocatedMatters: Matter[];
  onClose: () => void;
  onSuccess: () => void;
  preselectedMatterId?: string;
}

function OfferBriefModal({
  unallocatedMatters,
  onClose,
  onSuccess,
  preselectedMatterId,
}: OfferModalProps) {
  const [selectedMatterId, setSelectedMatterId] = useState(preselectedMatterId ?? '');
  const [selectedBarristerId, setSelectedBarristerId] = useState('');
  const [notes, setNotes] = useState('');

  const apiBarristers = useApiBarristersStore((s) => s.barristers);
  const barristersLoaded = useApiBarristersStore((s) => s.loaded);

  const queryClient = useQueryClient();

  const offerMutation = useMutation({
    mutationFn: () =>
      offerAllocation({
        caseId: selectedMatterId,
        barristerId: selectedBarristerId,
        clerkNote: notes.trim() || undefined,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['allocations'] });
      queryClient.invalidateQueries({ queryKey: ['matters'] });
      toast.success('Brief offered — barrister will be notified');
      onSuccess();
    },
    onError: (err: any) => {
      const msg =
        err?.response?.data?.message ??
        err?.response?.data?.error ??
        'Failed to offer brief';
      toast.error(msg);
    },
  });

  function handleSubmit() {
    if (!selectedMatterId) {
      toast.error('Select a matter');
      return;
    }
    if (!selectedBarristerId) {
      toast.error('Select a barrister');
      return;
    }
    offerMutation.mutate();
  }

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-black/40 p-4">
      <ScrollLock />
      <div className="flex min-h-full items-center justify-center">
        <div className="w-full max-w-lg rounded-xl border border-border bg-card p-6 shadow-xl">
          <div className="mb-5 flex items-center justify-between">
            <h2 className="text-lg font-semibold text-foreground">Offer brief</h2>
            <button
              onClick={onClose}
              className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          <div className="space-y-4">
            {/* Matter */}
            <div>
              <label className={LABEL_CLS}>Matter</label>
              <select
                className={INPUT_CLS}
                value={selectedMatterId}
                onChange={(e) => setSelectedMatterId(e.target.value)}
                disabled={!!preselectedMatterId}
              >
                <option value="">— Select matter —</option>
                {unallocatedMatters.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.number ? `${m.number} — ` : ''}{m.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Barrister */}
            <div>
              <label className={LABEL_CLS}>Barrister</label>
              {!barristersLoaded ? (
                <div className="h-10 animate-pulse rounded-md bg-muted" />
              ) : (
                <select
                  className={INPUT_CLS}
                  value={selectedBarristerId}
                  onChange={(e) => setSelectedBarristerId(e.target.value)}
                >
                  <option value="">— Select barrister —</option>
                  {apiBarristers.map((b) => {
                    const suffix = b.user?.memberType === 'silk' ? ' KC' : '';
                    const avail =
                      b.availability !== 'available'
                        ? ` (${b.availability.replace(/_/g, ' ')})`
                        : '';
                    return (
                      <option key={b.id} value={b.id}>
                        {b.fullName}{suffix}{avail}
                      </option>
                    );
                  })}
                </select>
              )}
            </div>

            {/* Clerk note */}
            <div>
              <label className={LABEL_CLS}>Clerk note (optional)</label>
              <textarea
                rows={3}
                className={`${INPUT_CLS} h-auto py-2`}
                placeholder="Any context for the barrister…"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
              />
            </div>
          </div>

          <div className="mt-6 flex justify-end gap-2">
            <Button variant="outline" size="md" onClick={onClose}>
              Cancel
            </Button>
            <Button
              variant="primary"
              size="md"
              onClick={handleSubmit}
              disabled={offerMutation.isPending}
            >
              {offerMutation.isPending ? 'Offering…' : 'Offer brief'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ─── Withdraw Modal ─────────────────────────────────────────── */

interface WithdrawModalProps {
  allocation: Allocation;
  onClose: () => void;
  onSuccess: () => void;
}

function WithdrawModal({ allocation, onClose, onSuccess }: WithdrawModalProps) {
  const [reason, setReason] = useState('');
  const queryClient = useQueryClient();

  const withdrawMutation = useMutation({
    mutationFn: () => withdrawAllocation(allocation.id, reason.trim() || undefined),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['allocations'] });
      toast.success('Offer withdrawn');
      onSuccess();
    },
    onError: (err: any) => {
      const msg =
        err?.response?.data?.message ??
        err?.response?.data?.error ??
        'Failed to withdraw offer';
      toast.error(msg);
    },
  });

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-black/40 p-4">
      <ScrollLock />
      <div className="flex min-h-full items-center justify-center">
        <div className="w-full max-w-md rounded-xl border border-border bg-card p-6 shadow-xl">
          <div className="mb-5 flex items-center justify-between">
            <h2 className="text-lg font-semibold text-foreground">Withdraw offer</h2>
            <button
              onClick={onClose}
              className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          <p className="mb-4 text-sm text-muted-foreground">
            Withdrawing offer for{' '}
            <span className="font-medium text-foreground">{matterDisplayName(allocation)}</span>{' '}
            offered to {barristerDisplayName(allocation)}.
          </p>

          <div className="mb-4">
            <label className={LABEL_CLS}>Reason (optional)</label>
            <textarea
              rows={3}
              className={`${INPUT_CLS} h-auto py-2`}
              placeholder="Reason for withdrawing this offer…"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
          </div>

          <div className="flex justify-end gap-2">
            <Button variant="outline" size="md" onClick={onClose}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              size="md"
              onClick={() => withdrawMutation.mutate()}
              disabled={withdrawMutation.isPending}
            >
              {withdrawMutation.isPending ? 'Withdrawing…' : 'Withdraw offer'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ─── Allocation Row ─────────────────────────────────────────── */

interface AllocationRowProps {
  allocation: Allocation;
  onWithdraw: (a: Allocation) => void;
  onReOffer: (matterId: string) => void;
  onNavigateMatter: (caseId: string) => void;
}

function AllocationRow({
  allocation,
  onWithdraw,
  onReOffer,
  onNavigateMatter,
}: AllocationRowProps) {
  const isOpen =
    allocation.status === 'pending' || allocation.status === 'conference_requested';
  const isTerminal =
    allocation.status === 'declined' || allocation.status === 'withdrawn';

  const declineLabel = allocation.declineReason
    ? DECLINE_REASON_LABEL[allocation.declineReason]
    : allocation.status === 'withdrawn'
    ? 'Withdrawn'
    : null;

  return (
    <tr>
      <td className="max-w-xs">
        <div className="font-medium text-foreground leading-snug">
          {allocation.case?.caseName ?? '—'}
        </div>
        {allocation.case?.caseNumber && (
          <div className="text-[11px] text-muted-foreground">{allocation.case.caseNumber}</div>
        )}
      </td>
      <td>
        <div className="text-sm text-foreground">{barristerDisplayName(allocation)}</div>
        {allocation.case?.practiceArea?.name && (
          <div className="text-[11px] text-muted-foreground">
            {allocation.case.practiceArea.name}
          </div>
        )}
      </td>
      <td className="text-xs text-muted-foreground">{formatDate(allocation.offeredAt)}</td>
      <td className="text-xs text-muted-foreground">
        {allocation.respondedAt
          ? formatDate(allocation.respondedAt)
          : allocation.respondByDate
          ? `Due ${formatDate(allocation.respondByDate)}`
          : '—'}
      </td>
      <td>{statusBadge(allocation.status)}</td>
      <td>
        {declineLabel && (
          <span className="text-xs text-muted-foreground">{declineLabel}</span>
        )}
        {allocation.barristerNote && !declineLabel && (
          <span className="text-xs text-muted-foreground italic">
            "{allocation.barristerNote}"
          </span>
        )}
      </td>
      <td>
        <div className="flex items-center gap-1">
          <RowActions
            onView={() => onNavigateMatter(allocation.caseId)}
            viewLabel="Open matter"
            onDelete={isOpen ? () => onWithdraw(allocation) : undefined}
            deleteLabel="Withdraw"
          />
          {isTerminal && (
            <button
              onClick={() => onReOffer(allocation.caseId)}
              title="Re-offer brief"
              aria-label="Re-offer brief"
              className="inline-flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
            >
              <Plus className="h-4 w-4" />
            </button>
          )}
        </div>
      </td>
    </tr>
  );
}

/* ─── Empty State ────────────────────────────────────────────── */

function EmptyState({ message }: { message: string }) {
  return (
    <tr>
      <td colSpan={7} className="py-12 text-center text-sm text-muted-foreground">
        {message}
      </td>
    </tr>
  );
}

/* ─── Skeleton Rows ──────────────────────────────────────────── */

function SkeletonRows() {
  return (
    <>
      {[1, 2, 3].map((i) => (
        <tr key={i}>
          {[1, 2, 3, 4, 5, 6, 7].map((j) => (
            <td key={j}>
              <div className="h-4 animate-pulse rounded bg-muted" />
            </td>
          ))}
        </tr>
      ))}
    </>
  );
}

/* ─── Page ───────────────────────────────────────────────────── */

export default function BookingsPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  // Routed under BOTH /admin/bookings and /clerk/bookings, so the base is derived, never
  // assumed (Rule #6) — a hardcoded '/clerk' sent every admin who opened a matter from here
  // to an address their shell does not serve.
  const user = useAuthStore((s) => s.user);
  const isClerk = user?.memberType === 'senior_clerk' || user?.memberType === 'junior_clerk';
  const basePath = isClerk ? '/clerk' : '/admin';

  // Ensure barristers are loaded for the offer modal
  const fetchBarristers = useApiBarristersStore((s) => s.fetch);
  const barristersLoaded = useApiBarristersStore((s) => s.loaded);
  useEffect(() => {
    if (!barristersLoaded) fetchBarristers();
  }, [barristersLoaded, fetchBarristers]);

  const [tab, setTab] = useState<Tab>('awaiting');
  const [search, setSearch] = useState('');
  const [showOfferModal, setShowOfferModal] = useState(false);
  const [offerPreselectedMatter, setOfferPreselectedMatter] = useState<string | undefined>();
  const [withdrawTarget, setWithdrawTarget] = useState<Allocation | null>(null);

  /* ── Data queries ── */

  // Awaiting-response tray: pending + conference_requested
  const awaitingQ = useQuery({
    queryKey: ['allocations', 'awaiting'],
    queryFn: listAwaiting,
    refetchInterval: 30_000,
  });

  // Accepted allocations
  const acceptedQ = useQuery({
    queryKey: ['allocations', 'accepted'],
    queryFn: () => listAllocations({ status: 'accepted', limit: 100 }),
    refetchInterval: 30_000,
  });

  // Declined + withdrawn
  const declinedQ = useQuery({
    queryKey: ['allocations', 'declined'],
    queryFn: () => listAllocations({ status: 'declined', limit: 100 }),
    refetchInterval: 30_000,
  });
  const withdrawnQ = useQuery({
    queryKey: ['allocations', 'withdrawn'],
    queryFn: () => listAllocations({ status: 'withdrawn', limit: 100 }),
    refetchInterval: 30_000,
  });

  // All allocations — for the "All" tab
  const allQ = useQuery({
    queryKey: ['allocations', 'all'],
    queryFn: () => listAllocations({ limit: 100 }),
    refetchInterval: 30_000,
    enabled: tab === 'all',
  });

  // Matters with status 'intake' — candidates for offering
  const intakeMattersQ = useQuery({
    queryKey: ['matters', 'intake'],
    queryFn: () => fetchMatters(),
    refetchInterval: 60_000,
    select: (res) =>
      res.matters.filter((m: Matter) => m.status === 'intake'),
  });

  /* ── Derived counts ── */

  const awaitingList = awaitingQ.data ?? [];
  const acceptedList = acceptedQ.data?.data ?? [];
  const declinedList = [
    ...(declinedQ.data?.data ?? []),
    ...(withdrawnQ.data?.data ?? []),
  ];
  const allList = allQ.data?.data ?? [];

  const unallocatedMatters: Matter[] = useMemo(() => {
    // intake matters that have no open/accepted allocation
    const allocatedCaseIds = new Set([
      ...awaitingList.map((a) => a.caseId),
      ...acceptedList.map((a) => a.caseId),
    ]);
    return (intakeMattersQ.data ?? []).filter((m: Matter) => !allocatedCaseIds.has(m.id));
  }, [awaitingList, acceptedList, intakeMattersQ.data]);

  /* ── Filtered list for current tab ── */

  const activeList: Allocation[] = useMemo(() => {
    const base =
      tab === 'awaiting'
        ? awaitingList
        : tab === 'accepted'
        ? acceptedList
        : tab === 'declined'
        ? declinedList
        : allList;

    if (!search.trim()) return base;
    const q = search.toLowerCase();
    return base.filter(
      (a) =>
        a.case?.caseName?.toLowerCase().includes(q) ||
        a.case?.caseNumber?.toLowerCase().includes(q) ||
        barristerDisplayName(a).toLowerCase().includes(q),
    );
  }, [tab, awaitingList, acceptedList, declinedList, allList, search]);

  const isLoading =
    (tab === 'awaiting' && awaitingQ.isLoading) ||
    (tab === 'accepted' && acceptedQ.isLoading) ||
    (tab === 'declined' && (declinedQ.isLoading || withdrawnQ.isLoading)) ||
    (tab === 'all' && allQ.isLoading);

  const isError =
    (tab === 'awaiting' && awaitingQ.isError) ||
    (tab === 'accepted' && acceptedQ.isError) ||
    (tab === 'declined' && declinedQ.isError) ||
    (tab === 'all' && allQ.isError);

  /* ── Tab definitions ── */

  const tabs: { key: Tab; label: string; count: number }[] = [
    { key: 'awaiting', label: 'Awaiting response', count: awaitingList.length },
    { key: 'accepted', label: 'Accepted', count: acceptedList.length },
    { key: 'declined', label: 'Declined / withdrawn', count: declinedList.length },
    { key: 'all', label: 'All', count: allQ.data?.pagination.total ?? 0 },
  ];

  /* ── Handlers ── */

  function handleOfferFromMatter(matterId: string) {
    setOfferPreselectedMatter(matterId);
    setShowOfferModal(true);
  }

  function handleNavigateMatter(caseId: string) {
    navigate(`${basePath}/matters/${caseId}`);
  }

  function handleWithdraw(allocation: Allocation) {
    setWithdrawTarget(allocation);
  }

  function handleReOffer(matterId: string) {
    setOfferPreselectedMatter(matterId);
    setShowOfferModal(true);
  }

  /* ── Empty state messages ── */

  const emptyMessages: Record<Tab, string> = {
    awaiting: 'No open offers — all briefs have been responded to.',
    accepted: 'No accepted briefs yet.',
    declined: 'No declined or withdrawn offers.',
    all: 'No allocation records found.',
  };

  /* ─── Render ─────────────────────────────────────────────── */

  return (
    <div>
      <PageHeader
        onBack={() => navigate(-1)}
        title="Brief pipeline"
        subtitle="Offers sent to barristers — track responses and manage the allocation lifecycle"
        actions={
          <Button
            variant="primary"
            size="md"
            leftIcon={<Plus className="h-4 w-4" />}
            onClick={() => {
              setOfferPreselectedMatter(undefined);
              setShowOfferModal(true);
            }}
          >
            Offer brief
          </Button>
        }
      />

      {/* ── Stat cards ── */}
      <div className="mb-6 grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-4">
        <StatCard
          icon={<Clock className="h-4 w-4" />}
          label="Awaiting response"
          value={String(awaitingList.length)}
          hint="pending + conference requested"
        />
        <StatCard
          icon={<CheckCircle className="h-4 w-4" />}
          label="Accepted"
          value={String(acceptedList.length)}
          hint="briefs in progress"
        />
        <StatCard
          icon={<Briefcase className="h-4 w-4" />}
          label="Unallocated"
          value={String(unallocatedMatters.length)}
          hint="intake matters without offer"
        />
        <StatCard
          icon={<Warning className="h-4 w-4" />}
          label="Declined"
          value={String(declinedList.length)}
          hint="may need re-offering"
        />
      </div>

      {/* ── Unallocated matters callout ── */}
      {unallocatedMatters.length > 0 && (
        <Card className="mb-4 border-amber-200 bg-amber-50 p-4 dark:border-amber-500/30 dark:bg-amber-500/10">
          <div className="flex items-start gap-3">
            <Warning className="mt-0.5 h-5 w-5 shrink-0 text-amber-600 dark:text-amber-400" />
            <div className="flex-1">
              <p className="text-sm font-medium text-amber-900 dark:text-amber-200">
                {unallocatedMatters.length} intake{' '}
                {unallocatedMatters.length === 1 ? 'matter needs' : 'matters need'} a barrister
              </p>
              <div className="mt-2 space-y-1.5">
                {unallocatedMatters.slice(0, 5).map((m) => (
                  <div key={m.id} className="flex items-center justify-between">
                    <span className="text-xs text-amber-800 dark:text-amber-300/90">
                      {m.number ? `${m.number} — ` : ''}
                      {m.name}
                    </span>
                    <Button
                      variant="outline"
                      size="xs"
                      onClick={() => handleOfferFromMatter(m.id)}
                      className="border-amber-300 text-amber-900 hover:bg-amber-100 dark:bg-amber-500/15 dark:border-amber-500/40 dark:text-amber-200 dark:hover:bg-amber-500/20"
                    >
                      Offer brief
                    </Button>
                  </div>
                ))}
                {unallocatedMatters.length > 5 && (
                  <p className="text-xs text-amber-700 dark:text-amber-400">
                    +{unallocatedMatters.length - 5} more
                  </p>
                )}
              </div>
            </div>
          </div>
        </Card>
      )}

      {/* ── Tabs ── */}
      {/* Five tabs with their counts are wider than a phone: "All" was sliced and
          "Declined / withdrawn" was off the screen entirely, with no way to reach either. The
          strip scrolls sideways now. The overlap pixel that tucks the active tab's underline
          into the strip's border moved from each BUTTON to the SCROLLER — on the button it
          protrudes a pixel below the box, which is enough to make `overflow-y` produce a
          vertical scrollbar inside the strip. */}
      <div className="no-scrollbar -mb-px mb-4 flex items-center gap-1 overflow-x-auto overflow-y-hidden border-b border-border">
        {tabs.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`shrink-0 whitespace-nowrap border-b-2 px-4 py-2.5 text-sm font-medium transition ${
              tab === t.key
                ? 'border-brand-600 text-foreground'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            {t.label}
            <span className="ml-1.5 rounded-full bg-muted px-1.5 py-0.5 text-[10px] tabular text-muted-foreground">
              {t.count}
            </span>
          </button>
        ))}
      </div>

      {/* ── Search bar ── */}
      <Card className="mb-4 p-3">
        <div className="flex flex-1 items-center gap-2 rounded-md border border-border bg-card px-3">
          <Search className="h-4 w-4 text-muted-foreground" />
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Filter by matter, barrister…"
            className="h-9 flex-1 bg-transparent text-sm placeholder:text-muted-foreground focus:outline-none"
          />
        </div>
      </Card>

      {/* ── Table ── */}
      <Card>
        <div className="overflow-x-auto">
          <table className="data-table">
            <thead>
              <tr>
                <th>Matter</th>
                <th>Barrister</th>
                <th>Offered</th>
                <th>Responded / due</th>
                <th>Status</th>
                <th>Note</th>
                <th className="w-10 whitespace-nowrap">Actions</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <SkeletonRows />
              ) : isError ? (
                <tr>
                  <td colSpan={7} className="py-12 text-center">
                    <p className="text-sm text-destructive">Failed to load allocations.</p>
                    <Button
                      variant="outline"
                      size="sm"
                      className="mt-3"
                      onClick={() => queryClient.invalidateQueries({ queryKey: ['allocations'] })}
                    >
                      Retry
                    </Button>
                  </td>
                </tr>
              ) : activeList.length === 0 ? (
                <EmptyState message={emptyMessages[tab]} />
              ) : (
                activeList.map((a) => (
                  <AllocationRow
                    key={a.id}
                    allocation={a}
                    onWithdraw={handleWithdraw}
                    onReOffer={handleReOffer}
                    onNavigateMatter={handleNavigateMatter}
                  />
                ))
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {/* ── Offer Brief Modal ── */}
      {showOfferModal && (
        <OfferBriefModal
          unallocatedMatters={unallocatedMatters}
          preselectedMatterId={offerPreselectedMatter}
          onClose={() => setShowOfferModal(false)}
          onSuccess={() => setShowOfferModal(false)}
        />
      )}

      {/* ── Withdraw Modal ── */}
      {withdrawTarget && (
        <WithdrawModal
          allocation={withdrawTarget}
          onClose={() => setWithdrawTarget(null)}
          onSuccess={() => setWithdrawTarget(null)}
        />
      )}
    </div>
  );
}
